import {of as observableOf, Observable} from 'rxjs';

import {first, map} from 'rxjs/operators';
import {MatDialogRef} from '@angular/material/dialog';
import {MatSelectChange} from '@angular/material/select';
import {ChangeDetectionStrategy, Component} from '@angular/core';
import {
  SourceAttribute,
  SourceCondition,
  SourceConstant,
  SourceCustomExpression,
  SourceFunction,
  SourceType,
} from './abstract-attribute-flow-control';
import {CustomExpressionParser} from '../../../../shared/custom-expression-parser/custom-expression-parser';
import {LanguageService} from '../../../../language/language.service';
import {DialogsService} from '../../../../shared/dialog/dialogs.service';
import {CacheService} from '../../../../cache/cache.service';

@Component({
  template: `
    <app-dialog>
      <app-dialog-title>
        <language
          key="CtrlAttributeFlowFunctionDialogTitle"
          defaultValue="Add/Edit Function"
        ></language>
      </app-dialog-title>

      <button
        app-dialog-header-button
        mat-icon-button
        (click)="save()"
        class="mat-icon-button-primary-color"
      >
        <mat-icon>check</mat-icon>
      </button>
      <button
        app-dialog-header-button
        mat-icon-button
        (click)="dialogRef.close(null)"
        class="mat-icon-button-error-color"
      >
        <mat-icon>undo</mat-icon>
      </button>
      <div mat-dialog-content>
        <mat-form-field>
          <mat-select [(ngModel)]="selectedFunction" (selectionChange)="functionSelected($event)">
            @for (fn of allFunctions; track fn) {
              <mat-option [value]="fn">
                <language
                  [key]="'CtrlAttributeFlowFunctionDialogFnName' + fn.id"
                  [defaultValue]="fn.id"
                ></language>
              </mat-option>
            }
          </mat-select>
          <mat-label>
            <language
              key="CtrlAttributeFlowFunctionDialogFnNamePlaceholder"
              defaultValue="Please select a function"
            ></language>
          </mat-label>
        </mat-form-field>
        @if (selectedFunction) {
          <p class="description">
            <language
              [key]="'CtrlAttributeFlowFunctionDialogFnDesc' + selectedFunction.id"
              [defaultValue]="selectedFunction.description"
            ></language>
          </p>
        }
        @if (selectedFunction) {
          <div class="arguments">
            @for (arg of selectedFunction.args; track arg; let argI = $index) {
              <div class="arg">
                <h5>
                  <span class="arg-name">{{ arg.name }}</span
                  ><span class="arg-type">{{ arg.type }}</span>
                </h5>
                <mat-form-field>
                  <mat-select
                    (selectionChange)="argSourceSelected(argI, $event)"
                    [value]="arguments[argI]"
                    [compareWith]="argSourceSelectCompare"
                  >
                    @for (optGrp of sourceOptions | async; track optGrp) {
                      <mat-optgroup [label]="optGrp.group">
                        @for (opt of optGrp.sources; track opt) {
                          <mat-option [value]="opt">
                            {{ opt.displayName }}
                          </mat-option>
                        }
                      </mat-optgroup>
                    }
                  </mat-select>
                  <mat-label>
                    <language
                      key="CtrlAttributeFlowFunctionDialogArgumentSourceLabel"
                      defaultValue="Argument"
                    ></language>
                  </mat-label>
                </mat-form-field>
                @if (isConstant(arguments[argI])) {
                  <mat-form-field>
                    <input
                      matInput
                      [type]="inputType(arguments[argI])"
                      [(ngModel)]="arguments[argI].value"
                    />
                    <mat-label>
                      <language
                        key="CtrlAttributeFlowConstantDialogValuePlaceholder"
                        defaultValue="Value of the constant"
                      ></language>
                    </mat-label>
                  </mat-form-field>
                }
                @if (isCustomExpression(arguments[argI])) {
                  <mat-form-field>
                    <textarea
                      matInput
                      [value]="arguments[argI].summary"
                      (blur)="parseCustomExpression($event, arguments[argI], argI)"
                    ></textarea>
                    <mat-label>
                      <language
                        key="CtrlAttributeFlowCustomExpressionDialogValuePlaceholder"
                        defaultValue="Expression"
                      ></language>
                    </mat-label>
                  </mat-form-field>
                }
              </div>
            }
          </div>
        }
      </div>
    </app-dialog>
  `,
  styleUrls: ['./attribute-flow-function-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AttributeFlowFunctionDialog {
  set function(value: SourceFunction) {
    if (value != null && value.functionName === '+') {
      throw 'Concat function not supported by dialog.';
    }

    this._function = value;
    this.arguments = value.arguments;

    // @ts-ignore because we fetch the "+" above.
    const index = this.allFunctions.map(f => f.id).indexOf(value.functionName);
    if (index !== -1) {
      this.selectedFunction = this.allFunctions[index];
    }
  }

  private _function: SourceFunction = new SourceFunction();

  selectedFunction: FunctionDefinition;

  functionSelected(event: MatSelectChange) {
    this.arguments = [];
    if (Array.isArray(event.value.args)) {
      event.value.args.forEach(() => {
        this.arguments.push(null);
      });
    }
  }

  sourceOptions: Observable<{group: string; sources: SourceType[]}[]>;

  set sourceAttributes(sourceAttributes: Observable<string[]>) {
    if (sourceAttributes == null) {
      sourceAttributes = observableOf([]);
    }
    this.sourceOptions = sourceAttributes.pipe(
      map(attributes => [
        {
          group: 'Attribute',
          sources: attributes.map(a => new SourceAttribute(a)),
        },
        {
          group: 'Constant Value',
          sources: [new SourceConstant('Number'), new SourceConstant('String')],
        },
        {
          group: 'Advanced',
          sources: [new SourceCustomExpression()],
        },
      ]),
    );
  }

  arguments: SourceType[] = [];

  save(): void {
    this._function.functionName = this.selectedFunction.id;
    this._function.arguments = this.arguments;
    this.dialogRef.close(this._function);
  }

  argSourceSelected(argIndex: number, event: MatSelectChange): void {
    if (
      event.value instanceof SourceType &&
      !this.argSourceSelectCompare(this.arguments[argIndex], event.value)
    ) {
      this.arguments[argIndex] = event.value.clone();
    } else {
      this.arguments[argIndex] = null;
    }
  }

  argSourceSelectCompare(optionValue: SourceType, selectionValue: SourceType): boolean {
    if (!(optionValue instanceof SourceType) || !(selectionValue instanceof SourceType)) {
      return false;
    } else if (
      optionValue instanceof SourceAttribute &&
      selectionValue instanceof SourceAttribute
    ) {
      return optionValue.name === selectionValue.name;
    } else if (optionValue instanceof SourceConstant && selectionValue instanceof SourceConstant) {
      return optionValue.type === selectionValue.type;
    } else if (
      optionValue instanceof SourceCustomExpression &&
      selectionValue instanceof SourceCustomExpression
    ) {
      return true;
    } else if (optionValue instanceof SourceFunction && selectionValue instanceof SourceFunction) {
      return true;
    } else {
      return false;
    }
  }

  parseCustomExpression(event: Event, arg: SourceCustomExpression, argIndex: number): void {
    // @ts-ignore
    const value = event.target.value;
    try {
      const expression: SourceCustomExpression = CustomExpressionParser.parse(value);
      if (expression === null) {
        throw 'Empty custom expression (null)';
      } else if (expression.customFunction instanceof SourceCondition) {
        if (this.selectedFunction.id !== 'IIF' && argIndex !== 0) {
          throw 'Condition not allowed';
        } else {
          //OK!
        }
      } else if (!(expression.customFunction instanceof SourceFunction)) {
        throw 'Only function allowed.';
      }

      // @ts-ignore
      arg.customFunction = expression.customFunction;
    } catch (e) {
      if (this.dialogsService != null && this.cacheService != null) {
        this.cacheService
          .getLanguageResource()
          .pipe(first())
          .subscribe(lr => {
            const title = LanguageService.translate(
              'CtrlAttributeFlowFunctionDialogCustomExpressionErrorTitle',
              'Error',
              lr,
            );
            let msg = LanguageService.translate(
              'CtrlAttributeFlowFunctionDialogCustomExpressionErrorMessage',
              'There occurred an error while parsing your custom expression.',
              lr,
            );
            this.dialogsService.webserviceError(e, title, msg, lr);
          });
      }
    }
  }

  isConstant(source: SourceType): boolean {
    return source instanceof SourceConstant;
  }

  isCustomExpression(source: SourceType): boolean {
    return source instanceof SourceCustomExpression;
  }

  inputType(source: SourceType): string {
    return source instanceof SourceConstant && source.type === 'Number' ? 'number' : 'text';
  }

  allFunctions: FunctionDefinition[] = [
    {
      id: 'BitAnd',
      description: 'The BitAnd function sets specified bits on a flag to 0.',
      args: [
        {name: 'mask', type: 'Integer'},
        {name: 'flag', type: 'Integer'},
      ],
    },
    {
      id: 'BitOr',
      description: 'The BitOr function sets specified bits on a flag to 1.',
      args: [
        {name: 'mask', type: 'Integer'},
        {name: 'flag', type: 'Integer'},
      ],
    },
    {
      id: 'ConvertSidToString',
      description:
        'ConvertSidToString converts a byte array containing a security identifier to a string.',
      args: [{name: 'ObjectSID', type: 'Binary'}],
    },
    {
      id: 'ConvertStringToGuid',
      description:
        'The ConvertStringToGuid function converts the string representation of a Guid to a binary representation of the Guid.',
      args: [{name: 'stringGuid', type: 'String'}],
    },
    {
      id: 'CRLF',
      description:
        'The CRLF generates a Carriage Return/Line Feed. This function will be used when the value users want to enter a new line.',
      args: [],
    },
    {
      id: 'DateTimeFormat',
      description:
        'The DateTimeFormat function is used to format a DateTime in string form to a specified format.',
      args: [
        {name: 'dateTimeString', type: 'String'},
        {name: 'format', type: 'String'},
      ],
    },
    {
      id: 'IIF',
      description:
        'The IIF function returns one of a set of possible values based on a specified condition.',
      args: [
        {name: 'condition', type: 'Boolean'},
        {name: 'valueTrue', type: 'Object'},
        {name: 'valueFalse', type: 'Object'},
      ],
    },
    {
      id: 'IsPresent',
      description:
        'The IsPresent function indicates whether the attribute is present in the resource.',
      args: [{name: 'attr', type: 'Object'}],
    },
    {
      id: 'Left',
      description:
        'The Left function returns a specified number of characters from the left side of a string.',
      args: [
        {name: 'str', type: 'String'},
        {name: 'numChars', type: 'Integer'},
      ],
    },
    {
      id: 'LeftPad',
      description:
        'The LeftPad function left-pads a string to a specified length using a provided padding character.',
      args: [
        {name: 'str', type: 'String'},
        {name: 'length', type: 'Integer'},
        {
          name: 'padCharacter',
          type: 'String',
        },
      ],
    },
    {
      id: 'LowerCase',
      description: 'The LowerCase function converts all characters in a string to lower case.',
      args: [{name: 'str', type: 'String'}],
    },
    {
      id: 'LTrim',
      description: 'The LTrim function removes leading white spaces from a string.',
      args: [{name: 'str', type: 'String'}],
    },
    {
      id: 'Mid',
      description:
        'The Mid function returns a specified number of characters from a specified position in a string.',
      args: [
        {name: 'str', type: 'String'},
        {name: 'pos', type: 'Integer'},
        {name: 'numChars', type: 'Integer'},
      ],
    },
    {
      id: 'Null',
      description:
        'The Null function returns null and indicates to the server to skip setting a value on the destination.',
      args: [],
    },
    {
      id: 'ProperCase',
      description:
        'The ProperCase function converts the first character of each space delimited word in a string to upper case, and all other characters are converted to lower case.',
      args: [{name: 'str', type: 'String'}],
    },
    {
      id: 'RandomNum',
      description: 'The RandomNum function returns a random number between a specified interval.',
      args: [
        {name: 'start', type: 'Integer'},
        {name: 'end', type: 'Integer'},
      ],
    },
    {
      id: 'ReplaceString',
      description:
        'The ReplaceString function substitutes the oldValue occurrences of a given attribute with newValue.',
      args: [
        {name: 'attribute', type: 'String'},
        {name: 'oldValue', type: 'String'},
        {
          name: 'newValue',
          type: 'String',
        },
      ],
    },
    {
      id: 'Right',
      description:
        'The Right function returns a specified number of characters from the right side of a string.',
      args: [
        {name: 'str', type: 'String'},
        {name: 'numChars', type: 'Integer'},
      ],
    },
    {
      id: 'RightPad',
      description:
        'The RightPad function right-pads a string to a specified length using a provided padding character.',
      args: [
        {name: 'str', type: 'String'},
        {name: 'length', type: 'Integer'},
        {
          name: 'padCharacter',
          type: 'String',
        },
      ],
    },
    {
      id: 'RTrim',
      description: 'The RTrim function removes trailing white spaces from a string.',
      args: [{name: 'str', type: 'String'}],
    },
    {
      id: 'Trim',
      description: 'The Trim function removes leading and trailing white spaces from a string.',
      args: [{name: 'str', type: 'String'}],
    },
    {
      id: 'UpperCase',
      description: 'The UpperCase function converts all characters in a string to upper case.',
      args: [{name: 'str', type: 'String'}],
    },
    {
      id: 'Word',
      description:
        'The Word function returns a word contained within a string, based on parameters describing the delimiters to use and the word number to return.',
      args: [
        {name: 'str', type: 'String'},
        {name: 'wordIndex', type: 'Integer'},
        {name: 'delimiters', type: 'String'},
      ],
    },
  ];

  constructor(
    public dialogRef: MatDialogRef<AttributeFlowFunctionDialog>,
    private dialogsService: DialogsService,
    private cacheService: CacheService,
  ) {}
}

class FunctionDefinition {
  id:
    | 'BitAnd'
    | 'BitOr'
    | 'ConvertSidToString'
    | 'ConvertStringToGuid'
    | 'CRLF'
    | 'DateTimeFormat'
    | 'IIF'
    | 'IsPresent'
    | 'Left'
    | 'LeftPad'
    | 'LowerCase'
    | 'LTrim'
    | 'Mid'
    | 'Null'
    | 'ProperCase'
    | 'RandomNum'
    | 'ReplaceString'
    | 'Right'
    | 'RightPad'
    | 'RTrim'
    | 'Trim'
    | 'UpperCase'
    | 'Word';
  description: string;
  args: {name: string; type: string}[];
}
